import {
  applyMapPageSignalsPatch,
} from "./map-page-signals.js";

export const MAP_SHELL_PROJECTION_VERSION = 1;
export const MAP_SHELL_PROJECTION_MAX_SERIALIZED_BYTES = 6 * 1024 * 1024;

const MAX_DEPTH = 32;
const MAX_NODES = 150_000;
const MAX_ARRAY_LENGTH = 8_192;
const MAX_OBJECT_KEYS = 8_192;
const MAX_KEY_LENGTH = 256;
const MAX_STRING_LENGTH = 262_144;
const SERIALIZED_BYTES_BY_VALUE = new WeakMap();
const OWNED_MAP_SHELL_PROJECTIONS = new WeakSet();
const TEXT_ENCODER = new TextEncoder();

const FIELD_BY_SIGNAL_ROOT = Object.freeze({
  _map_ui: "ui",
  _map_bridged: "bridged",
  _map_actions: "actions",
  _map_bookmarks: "bookmarks",
  _map_character_catalog: "characterCatalog",
  _map_character_presence: "characterPresence",
  _map_character_stream: "characterStream",
  _map_character_activity: "characterActivity",
  _map_character_activity_ui: "characterActivityUi",
  _map_character_activity_view: "characterActivityView",
  _map_session: "session",
  _map_runtime: "runtime",
  _shared_fish: "sharedFish",
});

export const MAP_SHELL_PROJECTION_SIGNAL_ROOTS = Object.freeze(
  Object.keys(FIELD_BY_SIGNAL_ROOT),
);

export const MAP_SHELL_PROJECTION_FIELDS = Object.freeze([
  ...Object.values(FIELD_BY_SIGNAL_ROOT),
]);

export const MAP_SHELL_PROJECTION_CONSUMER_SELECTOR = [
  "fishymap-query-input",
  "fishymap-bookmark-panel",
  "fishymap-info-panel",
  "fishymap-layer-panel",
  "fishymap-hover-tooltip",
  "fishymap-window-manager",
  "fishymap-tutorial-stage",
].join(",");

function isObject(value) {
  return Boolean(value) && typeof value === "object";
}

function copyJsonValue(value, budget, depth = 0) {
  if (depth > MAX_DEPTH) {
    throw new TypeError("MapShellProjectionV1 exceeds the maximum nesting depth");
  }
  budget.nodes += 1;
  if (budget.nodes > MAX_NODES) {
    throw new TypeError("MapShellProjectionV1 exceeds the maximum value count");
  }
  if (value === null || typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new TypeError("MapShellProjectionV1 numbers must be finite");
    }
    return value;
  }
  if (typeof value === "string") {
    if (value.length > MAX_STRING_LENGTH) {
      throw new TypeError("MapShellProjectionV1 contains an oversized string");
    }
    return value;
  }
  if (Array.isArray(value)) {
    if (value.length > MAX_ARRAY_LENGTH) {
      throw new TypeError("MapShellProjectionV1 contains an oversized array");
    }
    return value.map((entry) => copyJsonValue(entry, budget, depth + 1));
  }
  if (!isObject(value)) {
    throw new TypeError("MapShellProjectionV1 accepts JSON values only");
  }
  const keys = Object.keys(value);
  if (keys.length > MAX_OBJECT_KEYS) {
    throw new TypeError("MapShellProjectionV1 contains an oversized object");
  }
  const copy = {};
  for (const key of keys) {
    if (key === "__proto__") {
      throw new TypeError("MapShellProjectionV1 rejects prototype mutation keys");
    }
    if (key.length > MAX_KEY_LENGTH) {
      throw new TypeError("MapShellProjectionV1 contains an oversized key");
    }
    const entry = value[key];
    if (entry === undefined) {
      continue;
    }
    copy[key] = copyJsonValue(entry, budget, depth + 1);
  }
  return copy;
}

function deepFreeze(value) {
  if (!isObject(value) || Object.isFrozen(value)) {
    return value;
  }
  for (const entry of Object.values(value)) {
    deepFreeze(entry);
  }
  return Object.freeze(value);
}

function serializedByteLength(value) {
  const json = JSON.stringify(value);
  return TEXT_ENCODER.encode(json).byteLength;
}

function cachedSerializedByteLength(value) {
  if (!isObject(value)) {
    return serializedByteLength(value);
  }
  const cached = SERIALIZED_BYTES_BY_VALUE.get(value);
  if (cached != null) {
    return cached;
  }
  const bytes = serializedByteLength(value);
  if (Object.isFrozen(value)) {
    SERIALIZED_BYTES_BY_VALUE.set(value, bytes);
  }
  return bytes;
}

function projectionSerializedByteLength(projection) {
  let bytes = serializedByteLength({
    version: projection.version,
    revision: projection.revision,
  }) - 1;
  for (const field of MAP_SHELL_PROJECTION_FIELDS) {
    bytes += TEXT_ENCODER.encode(`,"${field}":`).byteLength;
    bytes += cachedSerializedByteLength(projection[field]);
  }
  return bytes + 1;
}

export function cloneBoundedMapShellValue(value) {
  const copy = copyJsonValue(value, { nodes: 0 });
  if (serializedByteLength(copy) > MAP_SHELL_PROJECTION_MAX_SERIALIZED_BYTES) {
    throw new TypeError("MapShellProjectionV1 exceeds its serialized byte bound");
  }
  return deepFreeze(copy);
}

function normalizeRevision(value) {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new TypeError(
      "MapShellProjectionV1 revision must be a non-negative safe integer",
    );
  }
  return value;
}

export function assertMapShellProjectionV1(value) {
  if (!isObject(value) || Array.isArray(value)) {
    throw new TypeError("Map shell projection must be an object");
  }
  if (value.version !== MAP_SHELL_PROJECTION_VERSION) {
    throw new TypeError("Unsupported map shell projection version");
  }
  normalizeRevision(value.revision);
  for (const field of MAP_SHELL_PROJECTION_FIELDS) {
    if (!isObject(value[field]) || Array.isArray(value[field])) {
      throw new TypeError(`MapShellProjectionV1 requires an object ${field} field`);
    }
  }
  const supportedFields = new Set([
    "version",
    "revision",
    ...MAP_SHELL_PROJECTION_FIELDS,
  ]);
  const unsupportedField = Object.keys(value).find((key) => !supportedFields.has(key));
  if (unsupportedField) {
    throw new TypeError(
      `MapShellProjectionV1 has unsupported field: ${unsupportedField}`,
    );
  }
  if (!OWNED_MAP_SHELL_PROJECTIONS.has(value)) {
    throw new TypeError(
      "MapShellProjectionV1 must come from the map shell projection owner",
    );
  }
  return value;
}

function projectionFromFields(fields, revision) {
  const projection = {
    version: MAP_SHELL_PROJECTION_VERSION,
    revision: normalizeRevision(revision),
  };
  for (const field of MAP_SHELL_PROJECTION_FIELDS) {
    projection[field] = fields[field] ?? Object.freeze({});
  }
  if (
    projectionSerializedByteLength(projection)
      > MAP_SHELL_PROJECTION_MAX_SERIALIZED_BYTES
  ) {
    throw new TypeError("MapShellProjectionV1 exceeds its serialized byte bound");
  }
  OWNED_MAP_SHELL_PROJECTIONS.add(projection);
  return Object.freeze(projection);
}

export function mapShellProjectionV1FromSignals(signals, { revision = 0 } = {}) {
  if (!isObject(signals) || Array.isArray(signals)) {
    throw new TypeError("MapShellProjectionV1 requires a signal-root object");
  }
  const budget = { nodes: 0 };
  const fields = {};
  for (const [root, field] of Object.entries(FIELD_BY_SIGNAL_ROOT)) {
    fields[field] = deepFreeze(copyJsonValue(signals[root] ?? {}, budget));
  }
  return projectionFromFields(fields, revision);
}

export function mapShellSignalStateV1FromProjection(projection) {
  assertMapShellProjectionV1(projection);
  const signals = {};
  for (const [root, field] of Object.entries(FIELD_BY_SIGNAL_ROOT)) {
    signals[root] = projection[field];
  }
  return Object.freeze(signals);
}

export function cloneBoundedMapShellSignalPatch(patch) {
  if (!isObject(patch) || Array.isArray(patch)) {
    throw new TypeError("Map shell signal patch must be an object");
  }
  const unsupportedRoot = Object.keys(patch).find(
    (root) => !Object.prototype.hasOwnProperty.call(FIELD_BY_SIGNAL_ROOT, root),
  );
  if (unsupportedRoot) {
    throw new TypeError(`Map shell signal patch has unsupported root: ${unsupportedRoot}`);
  }
  const copy = deepFreeze(copyJsonValue(patch, { nodes: 0 }));
  if (serializedByteLength(copy) > MAP_SHELL_PROJECTION_MAX_SERIALIZED_BYTES) {
    throw new TypeError("Map shell signal patch exceeds its serialized byte bound");
  }
  return copy;
}

function signalStateFromProjection(projection, rootsToClone) {
  const state = {};
  for (const [root, field] of Object.entries(FIELD_BY_SIGNAL_ROOT)) {
    state[root] = rootsToClone.has(root)
      ? copyJsonValue(projection[field], { nodes: 0 })
      : projection[field];
  }
  return state;
}

function projectionFieldsFromSignalState(state, previousProjection, touchedRoots) {
  const fields = {};
  for (const [root, field] of Object.entries(FIELD_BY_SIGNAL_ROOT)) {
    fields[field] = touchedRoots.has(root)
      ? deepFreeze(state[root])
      : previousProjection[field];
  }
  return fields;
}

export function patchMapShellProjectionV1(projection, patch, { revision } = {}) {
  assertMapShellProjectionV1(projection);
  if (!isObject(patch) || Array.isArray(patch)) {
    throw new TypeError("MapShellProjectionV1 patch must be an object");
  }
  const safePatch = copyJsonValue(patch, { nodes: 0 });
  const touchedRoots = new Set(Object.keys(safePatch));
  const unsupportedRoot = [...touchedRoots].find(
    (root) => !Object.prototype.hasOwnProperty.call(FIELD_BY_SIGNAL_ROOT, root),
  );
  if (unsupportedRoot) {
    throw new TypeError(`MapShellProjectionV1 patch has unsupported root: ${unsupportedRoot}`);
  }
  const nextRevision = revision ?? projection.revision + 1;
  if (!Number.isSafeInteger(nextRevision) || nextRevision <= projection.revision) {
    throw new TypeError("MapShellProjectionV1 patch revision must advance");
  }
  const state = signalStateFromProjection(projection, touchedRoots);
  applyMapPageSignalsPatch(state, safePatch);
  return projectionFromFields(
    projectionFieldsFromSignalState(state, projection, touchedRoots),
    nextRevision,
  );
}

export function createMapShellProjectionOwner(initialSignals) {
  let projection = mapShellProjectionV1FromSignals(initialSignals);
  const subscribers = new Set();

  function publish() {
    for (const subscriber of [...subscribers]) {
      subscriber(projection);
    }
  }

  return Object.freeze({
    current() {
      return projection;
    },
    patch(patch) {
      projection = patchMapShellProjectionV1(projection, patch);
      publish();
      return projection;
    },
    replaceFromSignals(signals) {
      projection = mapShellProjectionV1FromSignals(signals, {
        revision: projection.revision + 1,
      });
      publish();
      return projection;
    },
    subscribe(subscriber, { emitCurrent = true } = {}) {
      if (typeof subscriber !== "function") {
        throw new TypeError("MapShellProjectionV1 subscriber must be a function");
      }
      subscribers.add(subscriber);
      if (emitCurrent) {
        subscriber(projection);
      }
      return () => subscribers.delete(subscriber);
    },
  });
}

export function publishMapShellProjectionV1(shell, projection) {
  assertMapShellProjectionV1(projection);
  if (!shell || typeof shell.querySelectorAll !== "function") {
    throw new TypeError("MapShellProjectionV1 publisher requires the map shell");
  }
  shell.mapShellProjection = projection;
  const consumers = shell.querySelectorAll(MAP_SHELL_PROJECTION_CONSUMER_SELECTOR);
  for (const consumer of consumers) {
    consumer.mapShellProjection = projection;
  }
  return consumers.length;
}
